import type { EndUser, Agent } from '@/components/inbox/types'

const HTML_VARIABLE_REGEX = /<span[^>]*\bdata-variable\b[^>]*>.*?<\/span>/g

/**
 * Replaces HTML variable spans (from macro/outbound editors) with actual values.
 * Used when inserting a macro into the ChatEditor so the agent sees real data.
 */
export function interpolateVariables(
  html: string,
  endUser: EndUser,
  agent: Agent,
  organizationName?: string | null
): string {
  return html.replace(HTML_VARIABLE_REGEX, (match) => {
    const keyMatch = match.match(/data-key="([^"]*)"/)
    if (!keyMatch) return match

    const fallbackMatch = match.match(/data-fallback="([^"]*)"/)
    const value = resolveVariable(keyMatch[1], endUser, agent, organizationName)
    return value || fallbackMatch?.[1] || ''
  })
}

function resolveVariable(
  key: string,
  endUser: EndUser,
  agent: Agent,
  organizationName?: string | null
): string | null {
  switch (key) {
    case 'person.name':
      return endUser.name || null
    case 'person.first_name':
      return endUser.name?.split(' ')[0] || null
    case 'person.email':
      return endUser.email || null
    case 'person.phone':
      return endUser.phone || null
    case 'person.locale':
      return endUser.locale || null
    case 'person.first_seen_at':
      return formatDate(endUser.first_seen_at)
    case 'person.last_seen_at':
      return formatDate(endUser.last_seen_at)
    case 'person.web_sessions_count':
      return endUser.web_sessions_count != null ? String(endUser.web_sessions_count) : null
    case 'company.name':
      return endUser.company || null
    case 'author.name':
      return agent.name || null
    case 'author.email':
      return agent.email || null
    case 'app.name':
      return organizationName || null
    default:
      return null
  }
}

function formatDate(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null
  const date = new Date(dateStr)
  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  return `${day}/${month}/${date.getFullYear()}`
}
